import React, { useCallback, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';
import media from '../style/media';
import { useGetUserQuery } from '../services/userApi';
import { StorageKeys } from '../utils/storage';

import { useConnectSabaiMutation, useDisconnectSabaiMutation } from '../services/patientApi';

import {
  PageContainer,
  ContentWrapper,
  Header as LayoutHeader,
  Title,
  Subtitle as LayoutSubtitle,
  GlassCard,
  Grid,
  Field,
  FieldLabel,
  FieldValue
} from '../style/layout';
import { Phone, User as UserIcon, MapPin, Activity, Calendar, Mail, Globe, Map, MessageCircle, ArrowLeft } from 'lucide-react';

// Styled components
const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 24px;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 1.75rem;
  color: #fff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 8px 16px rgba(102, 126, 234, 0.25);
  border: 2px solid white;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const IconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: #3b82f6;
  transition: all 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const AccessDeniedMessage = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: rgba(254, 242, 242, 0.8);
  border: 1px solid rgba(254, 202, 202, 0.5);
  border-radius: 24px;
  color: #991b1b;

  h3 {
    color: #991b1b;
    margin-top: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  p {
    margin: 0.75rem 0;
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const Profile: React.FC = () => {
  const nav = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [sabaiStatus, setSabaiStatus] = useState<boolean | string | null | undefined>(null);
  const cameras = useCameras();

  // Get userId from localStorage
  const storedUser = localStorage.getItem(StorageKeys.AUTH_USER);
  const parsedUser = storedUser ? JSON.parse(storedUser) : null;
  const userId: string | null = parsedUser?.id ?? null;
  const isOrgType3 = parsedUser?.org_type === 3;

  // Fetch user data
  const { data: userResult, isLoading, isError, error } = useGetUserQuery(userId ?? '', {
    skip: !userId,
  });

  // Extract profile info safely
  const profileInfo = userResult?.data ?? undefined;
  const initials = `${profileInfo?.firstName?.[0] ?? ''}${profileInfo?.lastName?.[0] ?? ''}`;

  // Access denied check
  useEffect(() => {
    const referrer = document.referrer;
    const isFromSettings = referrer.includes('/settings') ||
      referrer === '' ||
      referrer.includes(window.location.origin);

    if (!isFromSettings) {
      setShowAccessDenied(true);
      const timer = setTimeout(() => nav('/settings'), 3000);
      return () => clearTimeout(timer);
    }
  }, [nav]);

  useEffect(() => {
    if (profileInfo) {
      let apiStatus = profileInfo.sabai_status;
      if (apiStatus === undefined || apiStatus === null) {
        const storedUser = localStorage.getItem(StorageKeys.AUTH_USER);
        const parsedUser = storedUser ? JSON.parse(storedUser) : null;
        apiStatus = parsedUser?.sabai_status;
      }
      setSabaiStatus(apiStatus);
    }
  }, [profileInfo]);

  const [connectSabai] = useConnectSabaiMutation();
  const [disconnectSabai] = useDisconnectSabaiMutation();

  const handleConnectSabai = useCallback(async () => {
    console.log('handleConnectSabai clicked', { profileInfo, sabaiStatus });
    if (!profileInfo) return;
    
    const storedUser = localStorage.getItem(StorageKeys.AUTH_USER);
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    const currentStatus = sabaiStatus;

    if (currentStatus !== true && currentStatus !== 'true') {
      const patientId = profileInfo.patient_id || localStorage.getItem(StorageKeys.PATIENT_ID);
      if (!patientId) {
        console.log('No patient_id found for connectSabai');
        return;
      }
      try {
        console.log('Calling connectSabai mutation with patient_id:', patientId);
        await connectSabai(patientId).unwrap();
        console.log('connectSabai successful');
        setSabaiStatus('true');
        const updatedUser = { ...parsedUser, sabai_status: 'true' };
        localStorage.setItem(StorageKeys.AUTH_USER, JSON.stringify(updatedUser));
      } catch (error) {
        console.error('Failed to connect Sabai:', error);
      }
    }
  }, [profileInfo, connectSabai, sabaiStatus]);

  const handleDisconnectSabai = useCallback(async () => {
    console.log('handleDisconnectSabai clicked', { profileInfo });
    const patientId = profileInfo?.patient_id || localStorage.getItem(StorageKeys.PATIENT_ID);
    if (!profileInfo || !patientId) {
      console.log('Early return in handleDisconnectSabai', { 
        profileInfoExists: !!profileInfo, 
        patientIdExists: !!patientId 
      });
      return;
    }
    
    try {
      console.log('Calling disconnectSabai mutation with patient_id:', patientId);
      await disconnectSabai(patientId).unwrap();
      console.log('disconnectSabai successful');
      setSabaiStatus('false');
      const storedUser = localStorage.getItem(StorageKeys.AUTH_USER);
      const parsedUser = storedUser ? JSON.parse(storedUser) : null;
      const updatedUser = { ...parsedUser, sabai_status: 'false' };
      localStorage.setItem(StorageKeys.AUTH_USER, JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Failed to disconnect Sabai:', error);
    }
  }, [profileInfo, disconnectSabai]);

  // Toggle settings
  const toggleSettings = useCallback(() => setIsSettingsOpen(prev => !prev), []);
  const handleCloseSettings = useCallback(() => setIsSettingsOpen(false), []);
  const updateLicenseStatus = useCallback((valid: boolean) => setIsLicenseValid(valid), []);

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />
      <ContentWrapper>
        {showAccessDenied ? (
          <GlassCard>
            <AccessDeniedMessage>
              <h3>Access Denied</h3>
              <p>Please access Profile through the Settings page.</p>
              <p>Redirecting you back to Settings...</p>
            </AccessDeniedMessage>
          </GlassCard>
        ) : isLoading ? (
          <GlassCard>
            <p>Loading user data...</p>
          </GlassCard>
        ) : isError || !profileInfo ? (
          <GlassCard>
            <p>Error loading user data. Please try again later.</p>
          </GlassCard>
        ) : (
          <GlassCard>
            <LayoutHeader>
              <button 
                onClick={() => nav('/settings')} 
                style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#64748b', fontSize: '0.9rem', fontWeight: 600, padding: '0', marginBottom: '1.5rem', transition: 'color 0.2s', fontFamily: 'inherit' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0f172a'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
              >
                <ArrowLeft size={18} /> Back to Settings
              </button>
              <ProfileHeader>
                <Avatar>{initials || 'U'}</Avatar>
                <div>
                  <Title style={{ fontSize: '2rem' }}>
                    {profileInfo.firstName} {profileInfo.lastName}
                  </Title>
                  <LayoutSubtitle>User Profile Details</LayoutSubtitle>
                </div>
              </ProfileHeader>
            </LayoutHeader>

            <Grid>
              <Field>
                <IconWrapper style={{ color: '#3b82f6' }}>
                  <Mail />
                </IconWrapper>
                <FieldLabel>Email Address</FieldLabel>
                <FieldValue>{profileInfo.email}</FieldValue>
              </Field>

              <Field>
                <IconWrapper style={{ color: '#10b981' }}>
                  <Phone />
                </IconWrapper>
                <FieldLabel>Phone Number</FieldLabel>
                <FieldValue>{profileInfo.phone}</FieldValue>
              </Field>

              <Field>
                <IconWrapper style={{ color: '#ec4899' }}>
                  <UserIcon />
                </IconWrapper>
                <FieldLabel>Account Role</FieldLabel>
                <FieldValue>{profileInfo.role_name}</FieldValue>
              </Field>

              <Field>
                <IconWrapper style={{ color: '#f59e0b' }}>
                  <Activity />
                </IconWrapper>
                <FieldLabel>Account Status</FieldLabel>
                <FieldValue>{profileInfo.status === '1' ? 'Active' : 'Inactive'}</FieldValue>
              </Field>

              <Field style={{ gridColumn: '1 / -1' }}>
                <IconWrapper style={{ color: '#6366f1' }}>
                  <MapPin />
                </IconWrapper>
                <FieldLabel>Address</FieldLabel>
                <FieldValue>{profileInfo.address}</FieldValue>
              </Field>

              <Field>
                <IconWrapper style={{ color: '#8b5cf6' }}>
                  <Map />
                </IconWrapper>
                <FieldLabel>City / State</FieldLabel>
                <FieldValue>{profileInfo.city_name}, {profileInfo.state_name}</FieldValue>
              </Field>

              <Field>
                <IconWrapper style={{ color: '#06b6d4' }}>
                  <Globe />
                </IconWrapper>
                <FieldLabel>Country</FieldLabel>
                <FieldValue>{profileInfo.country_name}</FieldValue>
              </Field>

              <Field>
                <IconWrapper style={{ color: '#475569' }}>
                  <Calendar />
                </IconWrapper>
                <FieldLabel>Member Since</FieldLabel>
                <FieldValue>{new Date(profileInfo.createdAt).toLocaleDateString()}</FieldValue>
              </Field>

              <Field>
                <IconWrapper style={{ color: '#94a3b8' }}>
                  <Calendar />
                </IconWrapper>
                <FieldLabel>Last Profile Update</FieldLabel>
                <FieldValue>{new Date(profileInfo.updatedAt).toLocaleDateString()}</FieldValue>
              </Field>

              {false && !isOrgType3 && (
                <Field style={{ gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <IconWrapper style={{ color: '#22c55e', background: '#dcfce7', margin: 0 }}>
                      <MessageCircle />
                    </IconWrapper>
                    <FieldValue>Connect with Sabai Health</FieldValue>
                  </div>

                  <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5', margin: '1rem 0 1.5rem', fontFamily: 'inherit' }}>
                    Sabai will analyse your health reports and speak with you directly on WhatsApp. It will not share your data with anyone else. You can disconnect at any time.
                  </p>

                  <div>
                    {(sabaiStatus !== false && sabaiStatus !== 'false') ? (
                      <button
                        onClick={handleDisconnectSabai}
                        style={{
                          width: '100%',
                          padding: '1rem',
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          borderRadius: '16px',
                          fontSize: '1rem',
                          fontWeight: 700,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.background = '#fee2e2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.background = '#fef2f2';
                        }}
                      >
                        Disconnect from Sabai
                      </button>
                    ) : (
                      <button
                        onClick={handleConnectSabai}
                        style={{
                          width: '100%',
                          padding: '1rem',
                          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '16px',
                          fontSize: '1rem',
                          fontWeight: 700,
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 4px 12px rgba(37, 211, 102, 0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 20px rgba(37, 211, 102, 0.35)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.25)';
                        }}
                      >
                        <MessageCircle size={20} />
                        {'Connect to Sabai'}
                      </button>
                    )}
                  </div>
                </Field>
              )}
            </Grid>
          </GlassCard>

        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default Profile;
